import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import numeral from 'numeral';
import { css } from '@bd/bdui-styles/emotion';
import { Image, menuToggler } from '@bd/bdcore-utilities';
import { Link } from '@bd/bdcore-named-routes';
import withData from '@bd/bdcore-data';
import { FavoriteToggle } from './Favorites';
import CompareButton from './CompareButton';
import { colors, typography } from '../styles';

const styles = css`
  width: calc(100% - 30px);
  margin: 0 15px 30px 15px;
  @media (min-width: 480px) {
    width: 340px;
    max-width: 340px;
  }
  .PlanCard_wrapper {
    display: flex;
    background: ${colors.white};
    box-shadow: 0 10px 15px 0 rgba(0,0,0,0.05);
    transition: all 0.3s ease-out 0s;
    position: relative;
    &:hover {
      transform: scale(1.02);
      transform-style: preserve-3d;
      box-shadow: 0 10px 15px 0 rgba(0,0,0,0.15);
      z-index: 3;
    }
  }
  .PlanCard_inner {
    width: 100%;
    background-color: ${colors.white};
  }
  .PlanCard_media {
    position: relative;
  }
  .PlanCard_image {
    width: 100%;
    height: auto;
  }
  .PlanCard_content {
    padding: 20px;
    position: relative;
  }
  .PlanCard_name {
    margin: 0 0 0 0;
    font-size: 20px;
    line-height: .8;
    font-weight: 700;
    color: ${colors.brand1};
    a {
      color: ${colors.brand1};
    }
  }
  .PlanCard_city {
    margin: 0 0 0 0;
    font-size: 15px;
    font-weight: 700;
    font-family: ${typography.Condensed};
    text-transform: uppercase;
    color: ${colors.brand1};
    a {
      color: ${colors.brand1};
    }
  }
  .PlanCard_iconList {
    display: flex;
    list-style: none;
    margin: 12px 0;
    padding: 0;
  }
  .PlanCard_iconListItem {
    text-align: center;
    font-size: 15px;
    font-weight: 300;
    font-family: ${typography.Condensed};
    margin: 0 12px 0 0;
  }
  .PlanCard_iconListIcon {
    margin: 0 0 4px 0;
    display: none;
  }
  .PlanCard_iconListValue {
    font-weight: 700;
    margin: 0 3px 0 0;
  }
  .PlanCard_linkIcon {
    width: 13px;
    height: 13px;
    margin: 5px 0 0 0;
  }
  .PlanCard_mapButton {
    width: 23px;
    height: 31px;
    background-image: url('/images/icon-map-pin-brand2.svg');
    background-position: center center;
    background-repeat: no-repeat;
    position: absolute;
    top: 20px;
    right: 20px;
  }
  .PlanCard_detailButton {
    height: 40px;
    width: 40px;
    border-radius: 5px;
    background-color: ${colors.brand3};
    background-image: url('/images/icon-arrow-right-white.svg');
    background-position: center center;
    background-repeat: no-repeat;
    position: absolute;
    right: 20px;
    bottom: 20px;
    z-index: 3;
    pointer-events: none;
  }
  .PlanCard_list1 {
    list-style: none;
    margin: 0 -20px;
    border-top: 1px solid ${colors.neutral};
    padding: 0 20px;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
  }
  .PlanCard_priceWrapper {
    padding: 12px 0;
    width: 60%;
  }
  .PlanCard_priceValue {
    font-size: 22px;
    line-height: 1;
    margin: 0;
    font-family: ${typography.Condensed};
    color: ${colors.body};
  }
  .PlanCard_status {
    font-size: 15px;
    line-height: 1;
    font-weight: 700;
    font-family: ${typography.Condensed};
  }
  .PlanCard_list2 {
    list-style: none;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    padding: 12px 0 0 0;
    margin: 0;
  }
  .PlanCard_list2Item {
    font-size: 15px;
    line-height: 1.2;
    &:first-child {
      width: 60%;
    }
    a {
      font-weight: 700;
    }
  }
  .PlanCard_communities {
    position: relative;
    z-index: 2;
    transform-style: flat;
  }
  .PlanCard_listHeader {
    display: block;
    cursor: pointer;
  }
  .PlanCard_communtiesList {
    position: absolute;
    top: 30px;
    background-color: ${colors.white};
    width: 100%;
    padding: 10px 15px;
    margin: 0;
    list-style: none;
    border: 1px solid ${colors.neutral};
    font-size: 13px;
    line-height: 1.2;
  }
  .PlanCard_communitiesListName {
    font-weight: 700;
    font-size: 15px;
  }

  .CommunityListCard_btnCompare {
    height: 40px;
    font-weight: 700;
    text-transform: uppercase;
    font-size: 13px;
    color: ${colors.body};
    letter-spacing: 2px;
    text-align: center;
    display: flex;
    align-items: center;
    cursor: pointer;
    &:focus {
      outline: none;
    }
    .CompareButton_indicator {
      display: inline-block;
      border: 1px solid black;
      width: 11px;
      height: 11px;
      margin-right: 10px;
    }

    .CompareButton_indicator-active {
      background-color: ${colors.brand3};
    }
  }
`;

function PlanCard({ community, plan, communities, activateMenu, isActiveMenu }) {
  return (
    <div className={styles}>
      <div className="PlanCard_wrapper">
        <div className="PlanCard_inner">
          <div className="PlanCard_media">
            <Link name="plan-detail" plan={plan} community={community}>
              <Image
                className="PlanCard_image"
                src={get(plan, 'elevationPhotos[0].contentUrl') || get(plan, 'photos[0].contentUrl') || '/images/logo-fullColor-comingSoon.png'}
                alt={get(plan, 'photos[0].name', get(plan, 'photos[0].caption', ''))}
                width={600}
                height={380}
                exact
              />
              <div className="PlanCard_detailButton" />
            </Link>
            <div className="PlanCard_detailButton" />
            <FavoriteToggle plan={plan} />
          </div>
          <div className="PlanCard_content">
            <h4 className="PlanCard_name">
              <Link name="plan-detail" plan={plan} community={community}>
                {get(plan, 'name')}
              </Link>
            </h4>
            <ul className="PlanCard_iconList">
              {
                get(plan, 'beds') &&
                <li className="PlanCard_iconListItem">
                  <span className="PlanCard_iconListIcon"><img src="/images/icon-beds.svg" alt="" /></span>
                  <span className="PlanCard_iconListValue">{get(plan, 'beds')}</span>
                  <span className="PlanCard_iconListLabel">Beds</span>
                </li>
              }
              {
                get(plan, 'bathsFull') &&
                <li className="PlanCard_iconListItem">
                  <span className="PlanCard_iconListIcon"><img src="/images/icon-baths.svg" alt="" /></span>
                  <span className="PlanCard_iconListValue">
                    {get(plan, 'bathsFull')} {get(plan, 'bathsHalf', 0) > 0 && '.5'}
                  </span>
                  <span className="PlanCard_iconListLabel">Baths</span>
                </li>
              }
              {
                get(plan, 'sqftLow') && get(plan, 'sqftHigh') &&
                <li className="PlanCard_iconListItem">
                  <span className="PlanCard_iconListIcon"><img src="/images/icon-sqft.svg" alt="" /></span>
                  <span className="PlanCard_iconListValue">
                    {numeral(get(plan, 'sqftLow')).format('0,0')}
                    {' - '}
                    {numeral(get(plan, 'sqftHigh')).format('0,0')}
                  </span>
                  <span className="PlanCard_iconListLabel">SQ FT</span>
                </li>
              }
              {
                get(plan, 'sqft') && !(get(plan, 'sqftLow') || get(plan, 'sqftLow')) &&
                <li className="PlanCard_iconListItem">
                  <span className="PlanCard_iconListIcon"><img src="/images/icon-sqft.svg" alt="" /></span>
                  <span className="PlanCard_iconListValue">{numeral(get(plan, 'sqft')).format('0,0')}</span>
                  <span className="PlanCard_iconListLabel">SQ FT</span>
                </li>
              }
            </ul>
            <ul className="PlanCard_list1">
              {
                get(plan, 'price') &&
                <li className="PlanCard_priceWrapper">
                  From <span className="PlanCard_priceValue">{numeral(get(plan, 'price')).format('$0,0')}</span>
                </li>
              }
              {
                !get(plan, 'price') &&
                <li className="PlanCard_priceWrapper">
                  <span className="PlanCard_priceValue">Quote Upon Request</span>
                </li>
              }
            </ul>
            {
              !community &&
              communities.length > 0 &&
              <div className="position-relative PlanCard_communities">
                {
                  communities.length === 1 &&
                  <span onClick={activateMenu('Dropdown')} onKeyDown={activateMenu('Dropdown')} className={`PlanCard_listHeader ${isActiveMenu('Dropdown') ? 'PlanCard_listHeaderAlt' : ''}`} role="button" tabIndex={0}>
                    Available In {communities.length} Community
                  </span>
                }
                {
                  communities.length > 1 &&
                  <span onClick={activateMenu('Dropdown')} onKeyDown={activateMenu('Dropdown')} className={`PlanCard_listHeader ${isActiveMenu('Dropdown') ? 'PlanCard_listHeaderAlt' : ''}`} role="button" tabIndex={0}>
                    Available In {communities.length} Communities
                  </span>
                }
                {
                  isActiveMenu('Dropdown') &&
                  <ul className="PlanCard_communtiesList list-unstyled">
                    {
                      communities.map(item => (
                        <li key={item._id}>
                          <Link name="plan-detail" community={item} plan={plan}>
                            <span className="PlanCard_communitiesListName">{get(item, 'name')}</span> {get(item, 'address.addressLocality')}, {get(item, 'address.addressRegion')}
                          </Link>
                        </li>
                      ))
                    }
                  </ul>
                }
              </div>
            }
            {/* <ul className="PlanCard_list2">
              {
                get(plan, 'name') &&
                <li className="PlanCard_list2Item">
                  Floor Plan<br />
                  <Link name="plan-detail" plan={plan}>{get(plan, 'name')}</Link>
                </li>
              }
            </ul> */}

            <CompareButton
              limit={5}
              className="CommunityListCard_btnCompare"
              type="plans"
              id={plan._id}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

PlanCard.propTypes = {
  communities: PropTypes.arrayOf(PropTypes.object).isRequired,
  community: PropTypes.object,
  plan: PropTypes.object.isRequired,
  activateMenu: PropTypes.func.isRequired,
  isActiveMenu: PropTypes.func.isRequired,
};

PlanCard.defaultProps = {
  community: undefined,
};

export default withData(
  {
    community: true,
    plan: true,
    communities: true,
  },
  {
    required: {
      plan: true,
      communities: true,
    },
  },
)(menuToggler(PlanCard));



// WEBPACK FOOTER //
// ./src/components/PlanCard.js